"use client";

import * as React from "react";
import {
    Button,
    Title,
    Text,
    TextInput,
    Group,
    Stack,
    Image,
    Center,
    LoadingOverlay,
    Code,
    Box,
    useMantineColorScheme
} from "@mantine/core";
import { setupMFA, enableMFA } from "../../apis/auth";
import type { UserMfaSetup } from "../../lib/types";

interface MFASetupFormProps {
    mfaToken: string;
    onSuccess: (token: string) => void;
    onCancel: () => void;
}

export function MFASetupForm({ mfaToken, onSuccess, onCancel }: MFASetupFormProps) {
    const { colorScheme } = useMantineColorScheme();
    const [setupData, setSetupData] = React.useState<UserMfaSetup | null>(null);
    const [code, setCode] = React.useState("");
    const [isLoading, setIsLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        const initSetup = async () => {
            setIsLoading(true);
            try {
                // Use the temporary MFA token to initialize setup
                const data = await setupMFA(mfaToken);
                setSetupData(data);
            } catch (err) {
                setError("Failed to initialize MFA setup.");
            } finally {
                setIsLoading(false);
            }
        };
        initSetup();
    }, [mfaToken]);

    const handleEnable = async () => {
        if (!setupData) return;
        setIsLoading(true);
        setError(null);
        try {
            // Verify code and enable for "web" app context
            const { success, token: finalToken } = await enableMFA(mfaToken, setupData.secret, code, "web");
            if (success && finalToken) {
                onSuccess(finalToken);
            } else {
                setError("Invalid verification code.");
            }
        } catch (err) {
            setError("Failed to enable MFA.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box
            p={30}
            w="100%"
            maw={400}
            style={{
                backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.01)' : 'rgba(255, 255, 255, 0.5)',
                borderRadius: 'var(--mantine-radius-md)',
                border: '1px solid var(--modal-border)',
                backdropFilter: colorScheme === 'light' ? 'blur(10px)' : undefined,
                boxShadow: 'var(--mantine-shadow-md)',
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            <LoadingOverlay visible={isLoading} />
            <Stack gap="md">
                <Title order={4}>Mandatory MFA Setup</Title>
                <Text size="sm" c="dimmed">
                    Your organization requires Multi-Factor Authentication for the Web Application.
                    Please scan the QR code to proceed.
                </Text>

                {error && (
                    <Text c="red" size="sm" ta="center">
                        {error}
                    </Text>
                )}

                <Box
                    p="md"
                    bg={colorScheme === 'dark' ? 'dark.8' : 'gray.0'}
                    style={{ borderRadius: 'var(--mantine-radius-md)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                >
                    {setupData?.qrCode ? (
                        <Image
                            src={setupData.qrCode}
                            alt="MFA QR Code"
                            w={160}
                            h={160}
                            p="xs"
                            bg="white"
                            style={{ borderRadius: 'var(--mantine-radius-sm)', marginBottom: 'var(--mantine-spacing-md)', boxShadow: 'var(--mantine-shadow-sm)' }}
                        />
                    ) : (
                        <Center w={160} h={160} mb="md" bg={colorScheme === 'dark' ? 'dark.7' : 'gray.1'} style={{ borderRadius: 'var(--mantine-radius-md)' }}>
                            <Text size="xs" c="dimmed">Generating QR...</Text>
                        </Center>
                    )}
                    <Text size="sm" ta="center" fw={500} mb="xs">
                        Scan with your authenticator app
                    </Text>
                    <Text size="xs" ta="center" c="dimmed" mb="md" maw={240}>
                        Alternative secret key:
                    </Text>
                    <Code block fz="xs" fw={700}>
                        {setupData?.secret}
                    </Code>
                </Box>

                <TextInput
                    label="Verification Code"
                    placeholder="000000"
                    value={code}
                    onChange={(e) => setCode(e.currentTarget.value)}
                    maxLength={6}
                    disabled={isLoading}
                    autoFocus
                />

                <Group mt="md">
                    <Button fullWidth onClick={handleEnable} disabled={isLoading || code.length !== 6} color="black" c="white">
                        Verify and Proceed
                    </Button>
                    <Button
                        fullWidth
                        variant="subtle"
                        onClick={onCancel}
                        disabled={isLoading}
                    >
                        Cancel
                    </Button>
                </Group>
            </Stack>
        </Box>
    );
}
